/**
 * ContactTagsSection Component
 * Displays and manages contact-level tags backed by the contact_tags table.
 * The DB trigger keeps contacts.tags array in sync for backward compatibility.
 */

import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getContactTags,
  addContactTag,
  removeContactTag,
  TAG_NOT_REMOVED_MESSAGE,
} from "../../services/contactTags.service";
import { useToast } from "../ui/Toast";
import type { TagSourceType } from "../../types/contactTag.types";
import type { UUID } from "../../types/common.types";

const SUGGESTED_TAGS = [
  "VIP",
  "At Risk",
  "Past Due",
  "Payment Plan",
  "Contacted",
  "No Contact",
  "Bad Number",
  "Email Only",
  "Spanish",
  "Priority",
  "Retention",
  "Cancelled",
  "Referral Source",
];

const SOURCE_BADGE: Record<
  TagSourceType,
  { label: string; className: string }
> = {
  manual: { label: "", className: "" },
  sequence: { label: "seq", className: "bg-blue-100 text-blue-700" },
  import: { label: "import", className: "bg-orange-100 text-orange-700" },
  form_handler: { label: "form", className: "bg-green-100 text-green-700" },
};

interface ContactTagsSectionProps {
  contactId: UUID;
  editable?: boolean;
}

const ContactTagsSection: React.FC<ContactTagsSectionProps> = ({
  contactId,
  editable = true,
}) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [showAddTag, setShowAddTag] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  const { data: tags = [] } = useQuery({
    queryKey: ["contact-tags", contactId],
    queryFn: () => getContactTags(contactId),
    enabled: !!contactId,
  });

  const addMutation = useMutation({
    mutationFn: (tag: string) =>
      addContactTag({ contact_id: contactId, tag, source_type: "manual" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contact-tags", contactId] });
    },
    onError: () => showToast("error", "Failed to add tag"),
  });

  const removeMutation = useMutation({
    mutationFn: (tag: string) => removeContactTag(contactId, tag),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contact-tags", contactId] });
    },
    onError: (err: unknown) =>
      showToast(
        "error",
        err instanceof Error && err.message === TAG_NOT_REMOVED_MESSAGE
          ? err.message
          : "Failed to remove tag",
      ),
  });

  const handleAddTag = (tag: string) => {
    const trimmed = tag.trim();
    if (!trimmed || tags.some((t) => t.tag === trimmed)) {
      return;
    }
    addMutation.mutate(trimmed);
    setNewTag("");
    setShowAddTag(false);
    setShowSuggestions(false);
  };

  const appliedTagNames = tags.map((t) => t.tag);
  const filteredSuggestions = SUGGESTED_TAGS.filter(
    (s) =>
      !appliedTagNames.includes(s) &&
      s.toLowerCase().includes(newTag.toLowerCase()),
  );

  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 flex-wrap">
        {tags.map((t) => {
          const badge =
            SOURCE_BADGE[t.source_type as TagSourceType] ?? SOURCE_BADGE.manual;
          return (
            <span
              key={t.id}
              className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium bg-blue-100 text-blue-800 rounded-full"
            >
              {t.tag}
              {badge.label && (
                <span className={`text-xs px-1 rounded ${badge.className}`}>
                  {badge.label}
                </span>
              )}
              {editable && (
                <button
                  onClick={() => removeMutation.mutate(t.tag)}
                  className="ml-1 text-blue-600 hover:text-blue-800"
                  disabled={removeMutation.isPending}
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </span>
          );
        })}

        {editable && (
          <>
            {showAddTag ? (
              <div className="relative">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => {
                    setNewTag(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newTag.trim()) {
                      handleAddTag(newTag);
                    } else if (e.key === "Escape") {
                      setShowAddTag(false);
                      setNewTag("");
                    }
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  placeholder="Type tag name..."
                  className="px-3 py-1 text-sm border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 w-40"
                  autoFocus
                />
                {showSuggestions && filteredSuggestions.length > 0 && (
                  <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10 max-h-48 overflow-y-auto">
                    {filteredSuggestions.map((s) => (
                      <button
                        key={s}
                        onClick={() => handleAddTag(s)}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
                <button
                  onClick={() => {
                    setShowAddTag(false);
                    setNewTag("");
                  }}
                  className="ml-2 text-gray-400 hover:text-gray-600"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowAddTag(true)}
                className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium text-gray-500 border border-dashed border-gray-300 rounded-full hover:border-gray-400 hover:text-gray-700"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add Tag
              </button>
            )}
          </>
        )}

        {tags.length === 0 && !showAddTag && !editable && (
          <span className="text-sm text-gray-400 italic">No tags</span>
        )}
      </div>
    </div>
  );
};

export default ContactTagsSection;
